import { useEffect, useMemo, useRef, useState } from "react";
import SVG from "../../Components/SVG";
import InputText from "../Trade/Components/InputText";
import {
  formatTrimmedNumber,
  getTradeIconUrls,
  isLocalTradeIcon,
  isValidDecimalInput,
  normalizeSymbolCode,
} from "../Trade/tradeUtils";

const DEFAULT_TRADE_CONTEXT = {
  symbolCode: "btcusdt",
  symbolLabel: "BTC/USDT",
  symbolSvg: "",
  side: "sell",
  tradeDrawdownLimit: 103.95,
  freeMargin: null,
  leverage: null,
  maxPriceDecimals: 4,
};
const VOLUME_DECIMALS = 2;
const SAFE_LOSS_BUFFER = 0.01;
const COPIED_RESULT_TIMEOUT_MS = 1500;

const normalizeDecimalInput = (value) =>
  String(value ?? "")
    .replace(/,/g, "")
    .trim();

const toNullableNumber = (value) => {
  const normalized = normalizeDecimalInput(value);

  if (!normalized || normalized === ".") {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatGroupedInteger = (value) =>
  value.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const formatGroupedDecimalInput = (value) => {
  const normalized = normalizeDecimalInput(value);

  if (!normalized) {
    return "";
  }

  const hasDecimal = normalized.includes(".");
  const [integerPart = "", decimalPart = ""] = normalized.split(".");
  const formattedInteger = integerPart ? formatGroupedInteger(integerPart) : "";

  if (!hasDecimal) {
    return formattedInteger;
  }

  if (!integerPart) {
    return `.${decimalPart}`;
  }

  return `${formattedInteger}.${decimalPart}`;
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";

  return `$${formatTrimmedNumber(Math.abs(num), {
    useGrouping: true,
    fallback: "-",
    maxFractionDigits: 2,
  })}`;
};

const formatResultNumber = (value, maxFractionDigits) =>
  formatTrimmedNumber(value, {
    useGrouping: true,
    fallback: "-",
    maxFractionDigits,
  });

const formatPlainNumber = (value, maxFractionDigits) =>
  formatTrimmedNumber(value, {
    useGrouping: false,
    fallback: "-",
    maxFractionDigits,
  });

const floorToDecimals = (value, decimals) => {
  const factor = 10 ** decimals;
  return Math.floor((value + Number.EPSILON) * factor) / factor;
};

const ceilToDecimals = (value, decimals) => {
  const factor = 10 ** decimals;
  return Math.ceil((value - Number.EPSILON) * factor) / factor;
};

const writeTextToClipboard = async (value) => {
  const text = String(value ?? "");
  if (!text) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.error("Clipboard API write failed:", error);
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch (error) {
    console.error("Fallback clipboard write failed:", error);
    return false;
  }
};

const getNormalizedSide = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "buy" || normalized === "long") return "buy";
  if (normalized === "sell" || normalized === "short") return "sell";
  return "";
};

const getLossFactor = ({ entryPrice, stopPrice, leverage, side }) => {
  if (
    !Number.isFinite(entryPrice) ||
    !Number.isFinite(stopPrice) ||
    !Number.isFinite(leverage) ||
    entryPrice <= 0 ||
    leverage <= 0
  ) {
    return null;
  }

  const normalizedSide = getNormalizedSide(side);
  if (!normalizedSide) return null;

  const adverseDistance =
    normalizedSide === "buy" ? entryPrice - stopPrice : stopPrice - entryPrice;

  if (adverseDistance <= 0) {
    return 0;
  }

  return Math.min((adverseDistance / entryPrice) * leverage, 1);
};

function CalculatorSymbolIcon({
  symbolCode,
  symbolSvg,
  alt,
  className = "size-3.5",
}) {
  const normalizedSymbol = normalizeSymbolCode(symbolCode);
  const iconUrls = useMemo(
    () => getTradeIconUrls(normalizedSymbol),
    [normalizedSymbol],
  );
  const [urlIndex, setUrlIndex] = useState(0);

  useEffect(() => {
    setUrlIndex(0);
  }, [normalizedSymbol, symbolSvg]);

  if (String(symbolSvg ?? "").trim()) {
    const svgSrc = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      String(symbolSvg).trim(),
    )}`;

    return (
      <img
        src={svgSrc}
        alt={alt}
        className={`${className} object-contain`}
        loading="lazy"
      />
    );
  }

  if (isLocalTradeIcon(normalizedSymbol)) {
    return <SVG name={normalizedSymbol} classname={className} />;
  }

  if (urlIndex < iconUrls.length) {
    return (
      <img
        src={iconUrls[urlIndex]}
        alt={alt}
        className={`${className} object-contain rounded-full`}
        loading="lazy"
        onError={() => setUrlIndex((prev) => prev + 1)}
      />
    );
  }

  return null;
}

function SummaryItem({ label, children }) {
  return (
    <div className="w-full flex flex-col sm:flex-row gap-0 sm:gap-3 items-center py-1 px-2 rounded-lg text-nowrap text-sm bg-zi-bg1-light dark:bg-zi-bg1-light/10 text-zi-text-light dark:text-zi-text-dark outline-none min-w-fit">
      <span className="text-start w-full sm:w-fit text-zi-text-light/50 dark:text-zi-text-dark/50">
        {label}
      </span>
      <span className="w-full flex items-center gap-1 justify-end">
        {children}
      </span>
    </div>
  );
}

function ResultBanner({ label, result, copied, onCopy }) {
  return (
    <div className="w-full flex items-center justify-center gap-2 mb-5 text-zi-text-dark dark:text-zi-text-dark py-2 bg-zi-gradient-button-blue dark:bg-zi-gradient-divider rounded-lg">
      <span className="font-thin">{label}</span>
      {result.kind === "value" ? (
        <p className="flex gap-1 items-center">
          <span className="font-bold">{result.display}</span>
          <span className="font-thin">{result.unit}</span>
        </p>
      ) : result.kind === "unlimited" ? (
        <p className="font-bold">محدودیتی وجود ندارد</p>
      ) : (
        <p className="font-bold">-</p>
      )}
      {result.kind === "value" && (
        <button
          type="button"
          onClick={onCopy}
          className="flex items-center justify-center"
          aria-label="کپی نتیجه محاسبه"
        >
          {copied ? (
            <SVG
              name="tick"
              classname="size-4 fill-zi-text-dark stroke-zi-blue-light"
            />
          ) : (
            <SVG
              name="copy"
              classname="size-4 stroke-zi-text-dark cursor-pointer"
            />
          )}
        </button>
      )}
    </div>
  );
}

export default function CalculatorPanel({
  onClose = () => {},
  onInsertValue = null,
  tradeContext = null,
}) {
  const [active, setActive] = useState("volume");
  const [volumeEntryPrice, setVolumeEntryPrice] = useState(null);
  const [volumeEntryPriceDisplay, setVolumeEntryPriceDisplay] = useState("");
  const [volumeStopLoss, setVolumeStopLoss] = useState(null);
  const [volumeStopLossDisplay, setVolumeStopLossDisplay] = useState("");
  const [stopLossEntryPrice, setStopLossEntryPrice] = useState(null);
  const [stopLossEntryPriceDisplay, setStopLossEntryPriceDisplay] =
    useState("");
  const [stopLossVolume, setStopLossVolume] = useState(null);
  const [stopLossVolumeDisplay, setStopLossVolumeDisplay] = useState("");
  const [copiedResultKey, setCopiedResultKey] = useState("");
  const copiedTimeoutRef = useRef(null);

  const effectiveTradeContext = {
    ...DEFAULT_TRADE_CONTEXT,
    ...(tradeContext && typeof tradeContext === "object" ? tradeContext : {}),
  };
  const side = getNormalizedSide(effectiveTradeContext.side);
  const sideLabel =
    side === "buy" ? "خرید" : side === "sell" ? "فروش" : "نامشخص";
  const leverage = Number(effectiveTradeContext.leverage);
  const freeMargin = Number(effectiveTradeContext.freeMargin);
  const maxPriceDecimalsRaw = Number(effectiveTradeContext.maxPriceDecimals);
  const maxPriceDecimals = Number.isFinite(maxPriceDecimalsRaw)
    ? Math.max(0, Math.floor(maxPriceDecimalsRaw))
    : 4;
  const tradeDrawdownLimit = Number(effectiveTradeContext.tradeDrawdownLimit);
  const allowedLoss =
    Number.isFinite(tradeDrawdownLimit) && tradeDrawdownLimit > 0
      ? Math.max(tradeDrawdownLimit - SAFE_LOSS_BUFFER, 0)
      : null;

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const handlePriceInputChange = (value, setPrice, setDisplay) => {
    const normalized = normalizeDecimalInput(value);

    if (!isValidDecimalInput(normalized, maxPriceDecimals)) {
      return;
    }

    setDisplay(formatGroupedDecimalInput(normalized));
    setPrice(toNullableNumber(normalized));
  };

  const handleVolumeInputChange = (value) => {
    const normalized = normalizeDecimalInput(value);

    if (!/^\d*\.?\d{0,2}$/.test(normalized)) {
      return;
    }

    const parsed = toNullableNumber(normalized);
    const hasFreeMargin = Number.isFinite(freeMargin) && freeMargin >= 0;

    if (hasFreeMargin && Number.isFinite(parsed) && parsed > freeMargin) {
      const factor = 10 ** VOLUME_DECIMALS;
      const roundedFreeMargin = Math.round(freeMargin * factor) / factor;
      const nextValue = String(roundedFreeMargin);

      setStopLossVolumeDisplay(formatGroupedDecimalInput(nextValue));
      setStopLossVolume(roundedFreeMargin);
      return;
    }

    setStopLossVolumeDisplay(formatGroupedDecimalInput(normalized));
    setStopLossVolume(parsed);
  };

  const volumeResult = useMemo(() => {
    if (
      allowedLoss === null ||
      !Number.isFinite(volumeEntryPrice) ||
      !Number.isFinite(volumeStopLoss) ||
      !Number.isFinite(leverage) ||
      !side
    ) {
      return { kind: "none" };
    }

    const lossFactor = getLossFactor({
      entryPrice: volumeEntryPrice,
      stopPrice: volumeStopLoss,
      leverage,
      side,
    });

    if (lossFactor === null) {
      return { kind: "none" };
    }

    if (lossFactor <= 0) {
      return { kind: "unlimited" };
    }

    const rawMaxVolume = allowedLoss / lossFactor;

    if (!Number.isFinite(rawMaxVolume) || rawMaxVolume < 0) {
      return { kind: "none" };
    }

    if (
      Number.isFinite(freeMargin) &&
      freeMargin > 0 &&
      rawMaxVolume >= freeMargin
    ) {
      return { kind: "unlimited" };
    }

    const safeVolume = floorToDecimals(rawMaxVolume, VOLUME_DECIMALS);

    return {
      kind: "value",
      field: "volume",
      unit: "USDT",
      display: formatResultNumber(safeVolume, VOLUME_DECIMALS),
      plain: formatPlainNumber(safeVolume, VOLUME_DECIMALS),
    };
  }, [
    allowedLoss,
    freeMargin,
    leverage,
    side,
    volumeEntryPrice,
    volumeStopLoss,
  ]);

  const stopLossResult = useMemo(() => {
    if (
      allowedLoss === null ||
      !Number.isFinite(stopLossEntryPrice) ||
      !Number.isFinite(stopLossVolume) ||
      !Number.isFinite(leverage) ||
      !side ||
      stopLossVolume <= 0
    ) {
      return { kind: "none" };
    }

    if (stopLossVolume <= allowedLoss) {
      return { kind: "unlimited" };
    }

    const allowedDistanceRatio = allowedLoss / (stopLossVolume * leverage);

    if (!Number.isFinite(allowedDistanceRatio) || allowedDistanceRatio <= 0) {
      return { kind: "none" };
    }

    const rawStopLoss =
      side === "buy"
        ? stopLossEntryPrice * (1 - allowedDistanceRatio)
        : stopLossEntryPrice * (1 + allowedDistanceRatio);

    if (!Number.isFinite(rawStopLoss) || rawStopLoss <= 0) {
      return { kind: "none" };
    }

    const safeStopLoss =
      side === "buy"
        ? ceilToDecimals(rawStopLoss, maxPriceDecimals)
        : floorToDecimals(rawStopLoss, maxPriceDecimals);

    return {
      kind: "value",
      field: "sl",
      unit: "USDT",
      display: formatResultNumber(safeStopLoss, maxPriceDecimals),
      plain: formatPlainNumber(safeStopLoss, maxPriceDecimals),
    };
  }, [
    allowedLoss,
    leverage,
    maxPriceDecimals,
    side,
    stopLossEntryPrice,
    stopLossVolume,
  ]);

  const activeResult = active === "volume" ? volumeResult : stopLossResult;
  const canInsert =
    activeResult.kind === "value" && typeof onInsertValue === "function";

  const handleCopy = async () => {
    if (activeResult.kind !== "value") return;

    const didCopy = await writeTextToClipboard(activeResult.plain);
    if (!didCopy) return;

    setCopiedResultKey(active);
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = setTimeout(() => {
      setCopiedResultKey("");
    }, COPIED_RESULT_TIMEOUT_MS);
  };

  const handleInsert = () => {
    if (activeResult.kind !== "value" || typeof onInsertValue !== "function") {
      return;
    }

    const values =
      active === "volume"
        ? {
            volume: activeResult.plain,
            sl: formatPlainNumber(volumeStopLoss, maxPriceDecimals),
          }
        : {
            volume: formatPlainNumber(stopLossVolume, VOLUME_DECIMALS),
            sl: activeResult.plain,
          };

    onInsertValue({
      field: activeResult.field,
      value: activeResult.plain,
      values,
    });
    onClose();
  };

  return (
    <section className="w-full max-w-2xl flex flex-col items-center gap-5 bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-xl p-5 shadow-md">
      <div className="w-full flex gap-5 rounded-xl border border-zi-border-dark bg-zi-bg2-light dark:bg-zi-bg2-dark p-1.5 overflow-auto">
        <button
          type="button"
          onClick={() => setActive("volume")}
          className={`w-full min-w-fit p-2 flex justify-center items-center text-sm rounded-lg transition-none ${active === "volume" ? "bg-zi-gradient-button-blue drop-shadow-md" : "bg-zi-bg1-light dark:bg-zi-bg1-dark hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"}`}
        >
          <span
            className={`text-nowrap ${active === "volume" ? "text-zi-text-dark drop-shadow-md" : "text-zi-text-light dark:text-zi-text-dark"}`}
          >
            محاسبه حجم معامله
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActive("stopLoss")}
          className={`w-full min-w-fit p-2 flex justify-center items-center text-sm rounded-lg transition-none ${active === "stopLoss" ? "bg-zi-gradient-button-blue drop-shadow-md" : "bg-zi-bg1-light dark:bg-zi-bg1-dark hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"}`}
        >
          <span
            className={`text-nowrap ${active === "stopLoss" ? "text-zi-text-dark drop-shadow-md" : "text-zi-text-light dark:text-zi-text-dark"}`}
          >
            محاسبه حد ضرر
          </span>
        </button>
      </div>

      <div className="w-full flex flex-col gap-5 overflow-auto">
        {active === "volume" ? (
          <>
            <p className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
              حداکثر حجم مجاز برای معامله خود را برای رد نشدن زیان مجاز هر
              معامله محاسبه کنید.
            </p>
            <div className="w-full flex gap-3 mb-5 overflow-x-auto min-h-fit">
              <SummaryItem label="نماد:">
                <span>{effectiveTradeContext.symbolLabel}</span>
                <CalculatorSymbolIcon
                  symbolCode={effectiveTradeContext.symbolCode}
                  symbolSvg={effectiveTradeContext.symbolSvg}
                  alt={effectiveTradeContext.symbolLabel}
                />
              </SummaryItem>
              <SummaryItem label="نوع پوزیشن:">
                <span>{sideLabel}</span>
              </SummaryItem>
              <SummaryItem label="زیان مجاز هر معامله:">
                <span>
                  {formatCurrency(effectiveTradeContext.tradeDrawdownLimit)}
                </span>
              </SummaryItem>
            </div>
            <p className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
              ابتدا مقادیر زیر را تکمیل کنید:
            </p>
            <form className="w-full flex flex-col sm:flex-row gap-3 sm:gap-3 mb-5">
              <div className="w-full flex flex-col gap-1.5 px-px">
                <label
                  htmlFor="calculator-volume-entry"
                  className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50"
                >
                  قیمت ورود به پوزیشن
                </label>
                <InputText
                  unit="USDT"
                  id="calculator-volume-entry"
                  name="calculator-volume-entry"
                  value={volumeEntryPriceDisplay}
                  onChange={(event) => {
                    handlePriceInputChange(
                      event.target.value,
                      setVolumeEntryPrice,
                      setVolumeEntryPriceDisplay,
                    );
                  }}
                />
              </div>
              <div className="w-full flex flex-col gap-1.5 px-px">
                <label
                  htmlFor="calculator-volume-stop-loss"
                  className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50"
                >
                  حد ضرر
                </label>
                <InputText
                  unit="USDT"
                  id="calculator-volume-stop-loss"
                  name="calculator-volume-stop-loss"
                  value={volumeStopLossDisplay}
                  onChange={(event) => {
                    handlePriceInputChange(
                      event.target.value,
                      setVolumeStopLoss,
                      setVolumeStopLossDisplay,
                    );
                  }}
                />
              </div>
            </form>
            <p className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
              حال می‌توانید این مقدار را کپی کنید یا برروی دکمه جانشانی بزنید.
            </p>
            <ResultBanner
              label="حداکثر حجم مجاز:"
              result={volumeResult}
              copied={copiedResultKey === "volume"}
              onCopy={handleCopy}
            />
            <div className="w-full flex items-center justify-center gap-3 text-nowrap">
              <button
                type="button"
                onClick={onClose}
                className="w-full px-5 py-2 bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark rounded-lg text-sm hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"
              >
                بستن
              </button>
              <button
                type="button"
                onClick={handleInsert}
                disabled={!canInsert}
                className={`w-full px-5 py-2 rounded-lg text-sm ${canInsert ? "bg-zi-gradient-button-yellow text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-offset-yellow-700 hocus:ring-offset-1 hocus:ring-zi-bg2-light" : "bg-gray-500 text-white/70 cursor-not-allowed"}`}
              >
                جانشانی
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
              بزرگترین حد ضرر مجاز برای معامله خود را برای رد نشدن زیان مجاز هر
              معامله محاسبه کنید.
            </p>
            <div className="w-full flex gap-3 mb-5 overflow-x-auto min-h-fit">
              <SummaryItem label="نماد:">
                <span>{effectiveTradeContext.symbolLabel}</span>
                <CalculatorSymbolIcon
                  symbolCode={effectiveTradeContext.symbolCode}
                  symbolSvg={effectiveTradeContext.symbolSvg}
                  alt={effectiveTradeContext.symbolLabel}
                />
              </SummaryItem>
              <SummaryItem label="نوع پوزیشن:">
                <span>{sideLabel}</span>
              </SummaryItem>
              <SummaryItem label="زیان مجاز هر معامله:">
                <span>
                  {formatCurrency(effectiveTradeContext.tradeDrawdownLimit)}
                </span>
              </SummaryItem>
            </div>
            <p className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
              ابتدا مقادیر زیر را تکمیل کنید:
            </p>
            <form className="w-full flex flex-col sm:flex-row gap-3 sm:gap-3 mb-5">
              <div className="w-full flex flex-col gap-1.5 px-px">
                <label
                  htmlFor="calculator-stop-loss-entry"
                  className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50"
                >
                  قیمت ورود به پوزیشن
                </label>
                <InputText
                  unit="USDT"
                  id="calculator-stop-loss-entry"
                  name="calculator-stop-loss-entry"
                  value={stopLossEntryPriceDisplay}
                  onChange={(event) => {
                    handlePriceInputChange(
                      event.target.value,
                      setStopLossEntryPrice,
                      setStopLossEntryPriceDisplay,
                    );
                  }}
                />
              </div>
              <div className="w-full flex flex-col gap-1.5 px-px">
                <label
                  htmlFor="calculator-stop-loss-volume"
                  className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50"
                >
                  حجم معامله
                </label>
                <InputText
                  unit="USDT"
                  id="calculator-stop-loss-volume"
                  name="calculator-stop-loss-volume"
                  value={stopLossVolumeDisplay}
                  onChange={(event) => {
                    handleVolumeInputChange(event.target.value);
                  }}
                />
              </div>
            </form>
            <p className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
              حال می‌توانید این مقدار را کپی کنید یا برروی دکمه جانشانی بزنید.
            </p>
            <ResultBanner
              label="بزرگترین حد ضرر مجاز:"
              result={stopLossResult}
              copied={copiedResultKey === "stopLoss"}
              onCopy={handleCopy}
            />
            <div className="w-full flex items-center justify-center gap-3 text-nowrap">
              <button
                type="button"
                onClick={onClose}
                className="w-full px-5 py-2 bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark rounded-lg text-sm hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"
              >
                بستن
              </button>
              <button
                type="button"
                onClick={handleInsert}
                disabled={!canInsert}
                className={`w-full px-5 py-2 rounded-lg text-sm ${canInsert ? "bg-zi-gradient-button-yellow text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-offset-yellow-700 hocus:ring-offset-1 hocus:ring-zi-bg2-light" : "bg-gray-500 text-white/70 cursor-not-allowed"}`}
              >
                جانشانی
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
